import { useFeatureFlagEnabled } from "@posthog/react";
import type { MentorshipDetails } from "@/lib/api/mentorships";
import {
  devOverridesEnabled,
  useDevOverridesStore,
} from "@/lib/hooks/admin/use-dev-overrides";
import { getPreferredName } from "@/lib/redacted-display";
import { isMagicMomentBannerExpired } from "@/lib/utils/magic-moments";

interface UseMentorMomentBannerStateArgs {
  mentorship?: MentorshipDetails | null;
}

export function useMentorMomentBannerState({
  mentorship,
}: UseMentorMomentBannerStateArgs) {
  const day1FlagEnabled = useFeatureFlagEnabled("enable-day1-banner");
  const day2FlagEnabled = useFeatureFlagEnabled("enable-day2-magic-moment");

  const rawDevDay = useDevOverridesStore((s) => s.magicMomentDay);
  const rawDevForceDay2Modal = useDevOverridesStore((s) => s.forceDay2Modal);
  const rawDevForceAtRiskMomentum = useDevOverridesStore(
    (s) => s.forceAtRiskMomentum,
  );

  const devDay = devOverridesEnabled ? rawDevDay : null;
  const devForceDay2Modal = devOverridesEnabled && rawDevForceDay2Modal;
  const devForceAtRiskMomentum =
    devOverridesEnabled && rawDevForceAtRiskMomentum;

  const magicMoment = mentorship?.metadata?.magic_moment;
  const dayState = devDay ?? magicMoment?.day_state;
  const day1Enabled = devDay ? true : day1FlagEnabled;
  const day2Enabled = devDay ? true : day2FlagEnabled;

  const modalDismissed =
    devDay === "day2"
      ? !devForceDay2Modal
      : !!magicMoment?.day2_modal_dismissed;

  const bannerDismissed = devDay
    ? false
    : dayState === "day2"
      ? !!magicMoment?.day2_banner_dismissed
      : !!magicMoment?.day1_banner_dismissed;

  const isBannerExpired =
    !devDay && isMagicMomentBannerExpired(dayState, magicMoment?.expires_at);

  const reactivation = mentorship?.metadata?.reactivation;
  const showReactivationBanner =
    !!reactivation && reactivation.banner_dismissed !== true;

  const atRiskMomentum = mentorship?.metadata?.at_risk_momentum;
  const showMomentumAlertBanner =
    devForceAtRiskMomentum || atRiskMomentum?.status === "active";

  return {
    dayState,
    day1Enabled,
    day2Enabled,
    modalDismissed,
    bannerDismissed,
    isBannerExpired,
    showMomentumAlertBanner,
    showReactivationBanner,
    teenGender: magicMoment?.teen_gender || null,
    teenId: mentorship?.mentee?.id || null,
    teenName: mentorship?.mentee
      ? getPreferredName(mentorship.mentee) || null
      : null,
  };
}
